import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

// Define available theme options
const themeOptions = {
  pricingGradient: {
    name: 'Pricing Gradient',
    primary: '#667eea',
    secondary: '#764ba2',
    description: 'Modern gradient theme matching pricing page',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    isGradient: true
  },
  blue: {
    name: 'Clinical Blue',
    primary: '#667eea', // Updated to match pricing page
    secondary: '#764ba2', // Updated to match pricing page
    description: 'Professional blue theme (default)'
  },
  green: {
    name: 'Medical Green',
    primary: '#4caf50',
    secondary: '#ff9800',
    description: 'Calming green for healthcare'
  },
  purple: {
    name: 'Modern Purple',
    primary: '#9c27b0',
    secondary: '#00bcd4',
    description: 'Contemporary purple theme'
  },
  teal: {
    name: 'Wellness Teal',
    primary: '#009688',
    secondary: '#ff5722',
    description: 'Refreshing teal for wellness'
  },
  indigo: {
    name: 'Deep Indigo',
    primary: '#303f9f',
    secondary: '#e91e63',
    description: 'Deep professional indigo'
  },
  orange: {
    name: 'Energetic Orange',
    primary: '#ff9800',
    secondary: '#2196f3',
    description: 'Warm and energetic orange'
  }
};

// Create the theme context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
export const AppThemeProvider = ({ children }) => {
  const [currentThemeKey, setCurrentThemeKey] = useState('pricingGradient');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('app_theme');
    if (savedTheme && themeOptions[savedTheme]) {
      setCurrentThemeKey(savedTheme);
    }
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('app_theme', currentThemeKey);
  }, [currentThemeKey]);

  // Memoize the current theme object to prevent infinite re-renders
  const currentTheme = useMemo(() => {
    return themeOptions[currentThemeKey];
  }, [currentThemeKey]);

  // Create the Material-UI theme based on current selection (memoized)
  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: currentTheme.primary,
      },
      secondary: {
        main: currentTheme.secondary,
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
      // Add custom gradient property
      gradient: currentTheme.gradient || `linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`,
    },
    typography: {
      fontFamily: [
        'Inter',
        'Roboto',
        'Arial',
        'sans-serif'
      ].join(','),
      fontWeight: 500,
    },
    components: {
      // Customize components to use the theme colors consistently
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            boxShadow: currentTheme.isGradient ? `0 4px 15px rgba(102, 126, 234, 0.3)` : '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
          },
          contained: {
            background: currentTheme.isGradient 
              ? `linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`
              : currentTheme.primary,
            '&:hover': {
              background: currentTheme.isGradient 
                ? `linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`
                : currentTheme.primary,
              transform: 'translateY(-2px)',
              boxShadow: currentTheme.isGradient ? `0 6px 20px rgba(102, 126, 234, 0.4)` : '0 4px 8px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: currentTheme.isGradient ? '0 20px 40px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: currentTheme.isGradient ? 'translateY(-2px)' : 'none',
              boxShadow: currentTheme.isGradient ? '0 25px 50px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: currentTheme.isGradient ? '0 20px 40px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            border: currentTheme.isGradient ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
            '&:hover': {
              transform: currentTheme.isGradient ? 'translateY(-5px)' : 'translateY(-2px)',
              boxShadow: currentTheme.isGradient ? '0 25px 50px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: currentTheme.isGradient 
              ? `linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`
              : currentTheme.primary,
            boxShadow: currentTheme.isGradient ? '0 4px 20px rgba(102, 126, 234, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: currentTheme.isGradient 
              ? 'rgba(255, 255, 255, 0.95)'
              : '#ffffff',
            backdropFilter: currentTheme.isGradient ? 'blur(10px)' : 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.3s ease',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: currentTheme.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: currentTheme.primary,
                boxShadow: currentTheme.isGradient ? `0 0 0 3px rgba(102, 126, 234, 0.1)` : 'none',
              },
            },
          },
        },
      },
    },
    // Add custom theme properties
    customTheme: {
      isGradient: currentTheme.isGradient,
      gradient: currentTheme.gradient,
      backdropFilter: currentTheme.isGradient ? 'blur(10px)' : 'none',
    },
  }), [currentTheme]);

  // Theme context value (memoized to prevent unnecessary re-renders)
  const value = useMemo(() => ({
    currentThemeKey,
    currentTheme,
    themeOptions,
    setTheme: setCurrentThemeKey,
    muiTheme: theme,
  }), [currentThemeKey, currentTheme, theme]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 